import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, PackageX } from "lucide-react";
import { BawainerCard } from "../components/cards";
import { Spinner, EmptyState } from "../components/common";
import RequestItemDialog from "../components/RequestItemDialog";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

export default function Explore() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [sort, setSort] = useState("boosted");
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reqTrip, setReqTrip] = useState(null);

  useEffect(() => { api.get("/meta").then((r) => setCats(r.data.categories)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (activeCat) p.set("category", activeCat);
    if (verifiedOnly) p.set("verified", "true");
    p.set("sort", sort);
    const { data } = await api.get(`/trips?${p.toString()}`);
    setTrips(data);
    setLoading(false);
  }, [q, activeCat, verifiedOnly, sort]);

  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [load]);

  return (
    <div className="space-y-5">
      <h1 className="font-heading font-extrabold text-2xl text-navy">{t("nav_explore")}</h1>

      <div className="bg-white rounded-full shadow-soft p-1.5 flex items-center">
        <Search className="ml-3 text-muted-foreground" size={18} />
        <input data-testid="explore-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={t("search_ph")} className="flex-1 bg-transparent px-3 py-2 text-sm outline-none" />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        <button onClick={() => setActiveCat("")} data-testid="cat-all"
          className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${!activeCat ? "bg-navy text-white" : "bg-white text-navy border border-black/5"}`}>All</button>
        {cats.map((c) => (
          <button key={c} onClick={() => setActiveCat(activeCat === c ? "" : c)} data-testid={`cat-${c}`}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${activeCat === c ? "bg-navy text-white" : "bg-white text-navy border border-black/5"}`}>{c}</button>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-sm font-medium text-navy bg-white rounded-full px-3 py-2 border border-black/5 cursor-pointer">
          <input type="checkbox" data-testid="verified-filter" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} className="accent-lime" />
          {t("verified_only")}
        </label>
        <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}
          className="text-sm font-medium text-navy bg-white rounded-full px-3 py-2 border border-black/5">
          <option value="boosted">Recommended</option>
          <option value="fee">Lowest fee</option>
          <option value="departure">Departing soon</option>
        </select>
        <span className="text-sm text-muted-foreground ml-auto flex items-center gap-1"><SlidersHorizontal size={14} /> {trips.length} results</span>
      </div>

      {loading ? <Spinner /> : trips.length === 0 ? (
        <EmptyState icon={PackageX} title={t("no_bawainer")} subtitle="Coba ubah filter atau posting request agar BAWAINER menghubungi kamu."
          action={<button onClick={() => navigate("/post-request")} data-testid="empty-post-request" className="rounded-full bg-lime text-navy font-bold px-5 py-2.5 text-sm">{t("post_request_instead")}</button>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {trips.map((tr) => (
            <BawainerCard key={tr.id} trip={tr} onRequest={user?.role === "titiper" ? setReqTrip : undefined} />
          ))}
        </div>
      )}
      {reqTrip && <RequestItemDialog trip={reqTrip} open={!!reqTrip} onOpenChange={(v) => !v && setReqTrip(null)} />}
    </div>
  );
}
